import { summarizeActivityChanges } from "@lumika/shared";
import type { ChangeValueResolvers } from "@lumika/shared";

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  // new Date("garbage") doesn't throw — it yields an invalid date that
  // renders as the literal "Invalid Date", so guard like the siblings below.
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * `formatDateTime`/`formatClockTime` pinned to an IANA zone instead of
 * the viewer's browser zone. Appointment times are STORE times — a
 * dealer abroad (or an admin in another state) must read the same wall
 * clock the customer was promised, so every appointment render goes
 * through these with the rooftop's timezone.
 */
export function formatDateTimeInZone(
  iso: string | null | undefined,
  timeZone: string,
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return date.toLocaleString(undefined, {
      timeZone,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    // Unknown zone string in the DB — show browser-local over nothing.
    return formatDateTime(iso);
  }
}

export function formatClockTimeInZone(
  iso: string | null | undefined,
  timeZone: string,
): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return date.toLocaleTimeString(undefined, {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return formatClockTime(iso);
  }
}

/**
 * Date-only values ("YYYY-MM-DD", e.g. birthdays) must be parsed as a
 * LOCAL date — `new Date("1990-06-09")` is parsed as UTC midnight and
 * renders one day early in any timezone west of Greenwich.
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) return "—";
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return "—";
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatCurrency(
  value: number | null | undefined,
  currency: string = "USD",
): string {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function humanize(value: string | null | undefined): string {
  if (!value) return "—";
  return value.replace(/_/g, " ");
}

/** Sentence-cased humanize for enum-ish values — "new_lead" → "New lead". */
export function labelize(value: string | null | undefined): string {
  const text = humanize(value);
  return text === "—" ? text : text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Activity summaries written before the senders learned to phrase
 * their failures for operators carry raw backend errors ("Telephony
 * channel <uuid> missing e164 sender number"). Those rows are already
 * persisted, so the timelines map the known patterns to the same
 * friendly text the backend writes today; anything unrecognized passes
 * through untouched.
 */
export function formatActivitySummary(
  summary: string | null | undefined,
): string | null {
  if (!summary) return null;
  if (summary.includes("missing e164 sender number")) {
    return "SMS not sent — this channel has no sender phone number configured. Check the Telephony tab in Settings.";
  }
  if (summary.includes("has no Telephony Account number for")) {
    return "SMS not sent — the phone number isn't provisioned yet. Check the Telephony tab in Settings.";
  }
  if (summary.includes("is not SMS-enabled")) {
    return "SMS not sent — the number's 10DLC registration is still pending approval. Check the Telephony tab in Settings.";
  }
  if (summary.includes("optout_sms=true")) {
    return "SMS not sent — this contact has opted out of SMS.";
  }
  return summary;
}

/**
 * Friendly display label for a notification event key. The dotted
 * `eventKey` enum (`ai.needs_help`, …) is preserved untouched — it
 * drives routing and per-event settings — this is purely the
 * human-facing label so an operator reads "Assistant needs help".
 */
const NOTIFICATION_EVENT_LABELS: Record<string, string> = {
  "ai.appointment.booked": "Appointment booked by Assistant",
  "ai.appointment.cancelled": "Appointment cancelled by Assistant",
  "ai.needs_help": "Assistant needs help",
  "ai.flagged_emergency": "Emergency flagged",
  "ai.no_response_timeout": "Assistant response timeout",
  "ai.customer_asked_for_manager": "Manager requested",
  "message.customer_replied": "Customer replied",
  "conversation.assigned_to_me": "Conversation assigned",
  "mention.note": "You were mentioned",
  "appointment.confirmed": "Appointment confirmed",
  "appointment.cancelled": "Appointment cancelled",
  "appointment.upcoming_30m": "Appointment soon",
  "appointment.missed": "Appointment missed",
  "followup.due": "Follow-up due",
  "task.assigned": "Task assigned",
  "task.due_soon": "Task due soon",
  "task.overdue": "Task overdue",
  "task.completed": "Task completed",
  "channel.unauthorized": "Channel disconnected",
  "channel.recovered": "Channel reconnected",
  "sla.breached": "SLA breached",
  "lead.snooze_woke": "Snoozed lead woke",
  "ai.draft_pending": "Assistant draft pending",
  "ai.budget_warning": "Assistant budget warning",
  "ai.budget_exhausted": "Assistant budget exhausted",
  "inventory.import_failing": "Inventory import failing",
  "aap.validation_failed": "Agent card validation failed",
};

export function notificationLabel(eventKey: string): string {
  // Unmapped keys fall back to the last dotted segment, humanized —
  // never the raw dotted enum.
  return (
    NOTIFICATION_EVENT_LABELS[eventKey] ??
    humanize(eventKey.split(".").pop() ?? eventKey)
  );
}

/**
 * Activity and notification bodies sometimes carry a structured diff —
 * `{"changes":{ field:{ before, after } }}` — that the backend writes
 * when an entity update produced a meaningful change. Rendered raw it's
 * unreadable JSON. Turn it into an explicit plain-English line with the
 * before → after values ("Status: Working → Lost"); return null when
 * the body is not a diff so callers fall back to the normal text.
 *
 * Pass resolvers (userName/channelName) where the caller has lookup
 * maps so ids render as names instead of neutral placeholders.
 */
export function summarizeChangeBody(
  body: string | null | undefined,
  resolvers: ChangeValueResolvers = {},
): string | null {
  return summarizeActivityChanges(body, {
    formatDateTime,
    ...resolvers,
  });
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  // Future timestamps mirror the past buckets with an "in …" prefix —
  // the old past-only ladder rendered EVERY future date as "just now"
  // (negative diff < 45), e.g. "Upcoming appointment just now" for a
  // booking two days out.
  if (diffSec < -44) {
    const aheadSec = -diffSec;
    const aheadMin = Math.round(aheadSec / 60);
    if (aheadMin < 60) return `in ${aheadMin}m`;
    const aheadHr = Math.round(aheadMin / 60);
    if (aheadHr < 24) return `in ${aheadHr}h`;
    const aheadDay = Math.round(aheadHr / 24);
    if (aheadDay < 7) return `in ${aheadDay}d`;
    const now = new Date();
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }
    return date.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" });
  }
  if (diffSec < 45) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  const now = new Date();
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" });
}

export function formatClockTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * Verbose relative time: "5 minutes ago" / "in 2 days". Used in
 * detail-page snapshot strips where a full sentence reads better than
 * the compact inbox form. (Before the dedupe into @lumika/ui the two
 * apps used the name `formatRelativeTime` for DIFFERENT renderings —
 * the compact inbox form above keeps that name; this is the
 * admin-frontend verbose variant under an explicit name.)
 */
export function formatVerboseRelativeTime(
  iso: string | null | undefined,
): string {
  if (!iso) return "—";
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "—";
  const diffMs = target - Date.now();
  const future = diffMs > 0;
  const seconds = Math.round(Math.abs(diffMs) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  const months = Math.round(days / 30);
  const years = Math.round(days / 365);
  let value: number;
  let unit: string;
  if (seconds < 60) {
    value = seconds;
    unit = "second";
  } else if (minutes < 60) {
    value = minutes;
    unit = "minute";
  } else if (hours < 24) {
    value = hours;
    unit = "hour";
  } else if (days < 30) {
    value = days;
    unit = "day";
  } else if (months < 12) {
    value = months;
    unit = "month";
  } else {
    value = years;
    unit = "year";
  }
  const plural = value === 1 ? unit : `${unit}s`;
  return future ? `in ${value} ${plural}` : `${value} ${plural} ago`;
}
